import { memo, useState, useMemo } from "react";
import { useGetUtilityUsersQuery, useAddStaffToDepartmentMutation } from "../../../../service/department-service";
import Button from "../../../../common/components/xepp/button/Button";
import CommonLoadingSpinner from "../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { toast } from "react-toastify";
import styles from "./AddMembersModal.module.scss";

type Props = {
	departmentId: string;
	existingStaffIds: string[];
	onClose: () => void;
	onSuccess: () => void;
};

const AddMembersModal = memo((props: Props) => {
	const { departmentId, existingStaffIds, onClose, onSuccess } = props;
	const { data: utilityUsers, isLoading, isError } = useGetUtilityUsersQuery();
	const [addStaff, { isLoading: isAdding }] = useAddStaffToDepartmentMutation();
	const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

	// Filter out users who are already staff members
	const availableUsers = useMemo(() => {
		if (!utilityUsers) return [];
		return utilityUsers.filter(user => !existingStaffIds.includes(user.id));
	}, [utilityUsers, existingStaffIds]);

	const handleUserToggle = (userId: string) => {
		setSelectedUsers((prev) =>
			prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
		);
	};

	const handleSubmit = async () => {
		if (selectedUsers.length === 0) {
			toast.error("Please select at least one user");
			return;
		}

		try {
			await addStaff({
				departmentId,
				userIds: selectedUsers,
			}).unwrap();

			toast.success("Members added successfully!");
			onSuccess();
			onClose();
		} catch (error: any) {
			toast.error(error?.data?.message || "Failed to add members. Please try again.");
		}
	};

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return (
		<div className={styles.backdrop} onClick={handleBackdropClick}>
			<div className={styles.modal}>
				<div className={styles.header}>
					<h2 className={styles.title}>Add Members</h2>
					<button className={styles.closeButton} onClick={onClose}>
						×
					</button>
				</div>

				{isError ? (
					<div className={styles.error}>
						Failed to load users. Please try again.
					</div>
				) : (
					<CommonLoadingSpinner loading={isLoading}>
						<div className={styles.content}>
							<p className={styles.instruction}>
								Select the users you want to add to this department:
							</p>
							{availableUsers.length === 0 ? (
								<div className={styles.noUsers}>
									All utility users are already members of this department.
								</div>
							) : (
								<div className={styles.userList}>
									{availableUsers.map((user) => (
										<label key={user.id} className={styles.userItem}>
											<input
												type="checkbox"
												checked={selectedUsers.includes(user.id)}
												onChange={() => handleUserToggle(user.id)}
												className={styles.checkbox}
											/>
											<span className={styles.userName}>{user.name}</span>
										</label>
									))}
								</div>
							)}
						</div>

						<div className={styles.actions}>
							<Button
								text="Cancel"
								variant="secondary"
								onClick={onClose}
								disabled={isAdding}
							/>
							<Button
								text={isAdding ? "Adding..." : `Add ${selectedUsers.length} Member${selectedUsers.length !== 1 ? 's' : ''}`}
								variant="primary"
								onClick={handleSubmit}
								disabled={selectedUsers.length === 0 || isAdding}
							/>
						</div>
					</CommonLoadingSpinner>
				)}
			</div>
		</div>
	);
});

export default AddMembersModal;
